import { gql } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";
import type {
  CurriculumHomework,
  TeamCurriculum,
  TeamCurriculumCheck,
  TeamCurriculumProgress,
  TeamCurriculumSummary,
  TeamCurriculumSummaryResult,
} from "./teams";
import type { TeamCurriculumLearningAsset } from "./learning-assets";

const CURRICULUM_FIELDS = gql`
  fragment YumesakuEngineerCurriculumFields on TeamCurriculum {
    id
    teamId
    lpCourseId
    sectionId
    title
    description
    content
    imageUrl
    lessonCoverUrl
    itemType
    lectureId
    courseId
    assignmentId
    displayOrder
    isPublished
    difficulty
    isBeta
    playgroundLanguage
    pathMode
    createdAt
    updatedAt
    homeworkCount
  }
`;

const LEARNING_ASSET_FIELDS = gql`
  fragment YumesakuEngineerLearningAssetFields on TeamCurriculumLearningAsset {
    kind
    relationType
    coverageRole
    required
    displayOrder
    course {
      id
      slug
      title
      thumbnailUrl
      description
      difficultyLevel
      estimatedHours
      lessonCount
    }
    lesson {
      id
      slug
      title
      duration
    }
  }
`;

export const YUMESAKU_ENGINEER_LESSON_SHELL_QUERY = gql`
  ${CURRICULUM_FIELDS}
  query YumesakuEngineerLessonShell(
    $teamId: String!
    $displayOrder: Int!
    $lpCourseId: String
    $defaultCourseOnly: Boolean!
  ) {
    curriculum: teamCurriculumByOrder(
      teamId: $teamId
      displayOrder: $displayOrder
      lpCourseId: $lpCourseId
      defaultCourseOnly: $defaultCourseOnly
    ) {
      ...YumesakuEngineerCurriculumFields
    }
    navigation: teamCurriculumNavigation(
      teamId: $teamId
      lpCourseId: $lpCourseId
    ) {
      id
      title
      itemType
      displayOrder
      pathMode
    }
  }
`;

export interface TeamCurriculumNavigationItem {
  id: string;
  title: string;
  description?: string | null;
  itemType: string;
  displayOrder: number;
  /** 学び方。null は通常、"read" は読むだけ、"optional" は任意 */
  pathMode?: string | null;
}

export interface YumesakuEngineerLessonShell {
  curriculum: TeamCurriculum | null;
  navigation: TeamCurriculumNavigationItem[];
}

export interface YumesakuEngineerPathLessonCourse {
  id: string;
  slug: string;
  title: string;
}

export interface YumesakuEngineerPathLessonNavigationItem
  extends TeamCurriculumNavigationItem {
  isCompleted: boolean;
}

export interface YumesakuEngineerPathLessonBundle {
  curriculum: TeamCurriculum;
  course: YumesakuEngineerPathLessonCourse | null;
  navigation: YumesakuEngineerPathLessonNavigationItem[];
  checks: TeamCurriculumCheck[];
  homeworks: CurriculumHomework[];
  learningAssets: TeamCurriculumLearningAsset[];
  /** このレッスンに付くミニテストの問題数。0 ならミニテストは無い */
  quizQuestionCount: number;
}

export const YUMESAKU_ENGINEER_PATH_LESSON_QUERY = gql`
  ${CURRICULUM_FIELDS}
  ${LEARNING_ASSET_FIELDS}
  query YumesakuEngineerPathLesson(
    $teamId: String!
    $displayOrder: Int!
    $lpCourseSlug: String
  ) {
    lesson: yumesakuEngineerPathLesson(
      teamId: $teamId
      displayOrder: $displayOrder
      lpCourseSlug: $lpCourseSlug
    ) {
      curriculum {
        ...YumesakuEngineerCurriculumFields
      }
      course {
        id
        slug
        title
      }
      navigation {
        id
        title
        itemType
        displayOrder
        isCompleted
        pathMode
      }
      checks {
        id
        curriculumId
        label
        displayOrder
        createdAt
        isCompleted
      }
      homeworks {
        id
        curriculumId
        question
        displayOrder
        createdAt
        userAnswer
      }
      learningAssets {
        ...YumesakuEngineerLearningAssetFields
      }
      quizQuestionCount
    }
  }
`;

/**
 * Loads the normal learner Path lesson in one Worker→Go request. A null slug
 * selects the default Path; named Path ownership/publication is resolved by
 * the API rather than by downloading every Path course first.
 */
export async function getYumesakuEngineerPathLesson(
  teamId: string,
  displayOrder: number,
  lpCourseSlug: string | null,
  options: GraphqlRequestConfig = {},
): Promise<YumesakuEngineerPathLessonBundle | null> {
  const client = createGraphqlClient(options);
  const data = await client.request<{
    lesson?: YumesakuEngineerPathLessonBundle | null;
  }>(YUMESAKU_ENGINEER_PATH_LESSON_QUERY, {
    teamId,
    displayOrder,
    lpCourseSlug,
  });
  return data.lesson ?? null;
}

export async function getYumesakuEngineerLessonShell(
  teamId: string,
  displayOrder: number,
  lpCourseId: string | null,
  defaultCourseOnly: boolean,
  options: GraphqlRequestConfig = {},
): Promise<YumesakuEngineerLessonShell> {
  const client = createGraphqlClient(options);
  const data = await client.request<YumesakuEngineerLessonShell>(
    YUMESAKU_ENGINEER_LESSON_SHELL_QUERY,
    { teamId, displayOrder, lpCourseId, defaultCourseOnly },
  );
  return {
    curriculum: data.curriculum ?? null,
    navigation: data.navigation ?? [],
  };
}

export const YUMESAKU_ENGINEER_PATH_PAGE_QUERY = gql`
  query YumesakuEngineerPathPage(
    $teamId: String!
    $lpCourseId: String
    $limit: Int!
    $offset: Int!
  ) {
    pathPage: teamCurriculumPathPage(
      teamId: $teamId
      lpCourseId: $lpCourseId
      limit: $limit
      offset: $offset
    ) {
      page {
        items {
          id
          lpCourseId
          sectionId
          title
          description
          imageUrl
          itemType
          lectureId
          courseId
          assignmentId
          displayOrder
          isPublished
          difficulty
          isBeta
          playgroundLanguage
          homeworkCount
          pathMode
        }
        totalCount
      }
      navigation {
        id
        title
        description
        itemType
        displayOrder
        pathMode
      }
    }
  }
`;

export interface YumesakuEngineerPathPage {
  page: TeamCurriculumSummaryResult;
  navigation: TeamCurriculumNavigationItem[];
}

/**
 * Loads one bounded card page and the content-free full Path outline in one
 * GraphQL operation. Quiz rows stay in navigation for section checkpoints but
 * are excluded before the page LIMIT/OFFSET and totalCount are calculated.
 */
export async function getYumesakuEngineerPathPage(
  teamId: string,
  lpCourseId: string | null,
  limit: number,
  offset: number,
  options: GraphqlRequestConfig = {},
): Promise<YumesakuEngineerPathPage> {
  const client = createGraphqlClient(options);
  const data = await client.request<{
    pathPage?: {
      page?: {
        items?: TeamCurriculumSummary[];
        totalCount?: number;
      } | null;
      navigation?: TeamCurriculumNavigationItem[] | null;
    } | null;
  }>(YUMESAKU_ENGINEER_PATH_PAGE_QUERY, {
    teamId,
    lpCourseId,
    limit,
    offset,
  });
  return {
    page: {
      items: data.pathPage?.page?.items ?? [],
      totalCount: data.pathPage?.page?.totalCount ?? 0,
    },
    navigation: data.pathPage?.navigation ?? [],
  };
}

export const YUMESAKU_ENGINEER_LESSON_RELATIONS_QUERY = gql`
  ${LEARNING_ASSET_FIELDS}
  query YumesakuEngineerLessonRelations(
    $curriculumId: String!
    $teamId: String!
    $includeLearningAssets: Boolean!
  ) {
    checks: curriculumChecks(curriculumId: $curriculumId) {
      id
      curriculumId
      label
      displayOrder
      createdAt
      isCompleted
    }
    homeworks: curriculumHomeworks(curriculumId: $curriculumId) {
      id
      curriculumId
      question
      displayOrder
      createdAt
      userAnswer
    }
    learningAssets: teamCurriculumLearningAssets(curriculumId: $curriculumId)
      @include(if: $includeLearningAssets) {
      ...YumesakuEngineerLearningAssetFields
    }
    curriculumProgress: myCurriculumProgress(teamId: $teamId) {
      id
      curriculumId
      userId
      isCompleted
      completedAt
      createdAt
      updatedAt
    }
  }
`;

export interface YumesakuEngineerLessonRelations {
  checks: TeamCurriculumCheck[];
  homeworks: CurriculumHomework[];
  learningAssets: TeamCurriculumLearningAsset[];
  curriculumProgress: TeamCurriculumProgress[];
}

export async function getYumesakuEngineerLessonRelations(
  curriculumId: string,
  teamId: string,
  includeLearningAssets: boolean,
  options: GraphqlRequestConfig = {},
): Promise<YumesakuEngineerLessonRelations> {
  const client = createGraphqlClient(options);
  const data = await client.request<YumesakuEngineerLessonRelations>(
    YUMESAKU_ENGINEER_LESSON_RELATIONS_QUERY,
    { curriculumId, teamId, includeLearningAssets },
  );
  return {
    checks: data.checks ?? [],
    homeworks: data.homeworks ?? [],
    learningAssets: data.learningAssets ?? [],
    curriculumProgress: data.curriculumProgress ?? [],
  };
}
